import { type Static, Type } from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";
import {
  BusinessOperationTraceV1Schema,
  isBusinessOperationTraceV1,
} from "./business-operation.js";
import { isOrderNumberForOrderId, OrderNumberSchema } from "./order-number.js";

const PaymentIdentifierSchema = Type.String({
  minLength: 8,
  maxLength: 128,
  pattern: "^[A-Za-z0-9][A-Za-z0-9._:-]{7,127}$",
});
const ProviderCodeSchema = Type.String({
  minLength: 2,
  maxLength: 32,
  pattern: "^[A-Z][A-Z0-9_]{1,31}$",
});
const ProviderReferenceSchema = Type.String({
  minLength: 1,
  maxLength: 128,
  pattern: "^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$",
});
const CanonicalDateTimeSchema = Type.String({
  minLength: 24,
  maxLength: 24,
  pattern: "^[0-9]{4}-[0-9]{2}-[0-9]{2}T[0-9]{2}:[0-9]{2}:[0-9]{2}\\.[0-9]{3}Z$",
});
const BusinessDateSchema = Type.String({ pattern: "^[0-9]{4}-[0-9]{2}-[0-9]{2}$" });
const MoneyFenSchema = Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER });

export const OfficialPaymentRailSchema = Type.Union([
  Type.Literal("WECHAT_PAY"),
  Type.Literal("ALIPAY"),
]);
export type OfficialPaymentRail = Static<typeof OfficialPaymentRailSchema>;

export const OfficialPaymentProviderCapabilitiesSchema = Type.Object(
  {
    create: Type.Boolean(),
    query: Type.Boolean(),
    cancel: Type.Boolean(),
    refund: Type.Boolean(),
    callback: Type.Boolean(),
    bill: Type.Boolean(),
    evidence: Type.Union([
      Type.Literal("NOT_VERIFIED"),
      Type.Literal("SANDBOX"),
      Type.Literal("PROVIDER_ACCEPTED"),
    ]),
  },
  { additionalProperties: false },
);
export type OfficialPaymentProviderCapabilities = Static<
  typeof OfficialPaymentProviderCapabilitiesSchema
>;

export const OfficialPaymentCreateCommandV1Schema = Type.Object(
  {
    contractVersion: Type.Literal("1.0.0"),
    attemptId: PaymentIdentifierSchema,
    tenantId: PaymentIdentifierSchema,
    storeId: PaymentIdentifierSchema,
    orderId: PaymentIdentifierSchema,
    orderNumber: OrderNumberSchema,
    businessDate: BusinessDateSchema,
    providerCode: ProviderCodeSchema,
    rail: OfficialPaymentRailSchema,
    amountFen: MoneyFenSchema,
    expectedOrderVersion: Type.Integer({ minimum: 1 }),
    currency: Type.Literal("CNY"),
    subject: Type.String({ minLength: 1, maxLength: 80 }),
    expiresAt: CanonicalDateTimeSchema,
    idempotencyKey: Type.String({ minLength: 8, maxLength: 128 }),
    operationTrace: BusinessOperationTraceV1Schema,
  },
  { additionalProperties: false },
);
export type OfficialPaymentCreateCommandV1 = Static<typeof OfficialPaymentCreateCommandV1Schema>;

export const OfficialPaymentAttemptCommandV1Schema = Type.Object(
  {
    contractVersion: Type.Literal("1.0.0"),
    action: Type.Union([Type.Literal("QUERY"), Type.Literal("CANCEL")]),
    attemptId: PaymentIdentifierSchema,
    tenantId: PaymentIdentifierSchema,
    storeId: PaymentIdentifierSchema,
    orderId: PaymentIdentifierSchema,
    orderNumber: OrderNumberSchema,
    businessDate: BusinessDateSchema,
    providerCode: ProviderCodeSchema,
    rail: OfficialPaymentRailSchema,
    providerTransactionReference: Type.Union([ProviderReferenceSchema, Type.Null()]),
    expectedAttemptVersion: Type.Integer({ minimum: 1 }),
    idempotencyKey: Type.String({ minLength: 8, maxLength: 128 }),
    operationTrace: BusinessOperationTraceV1Schema,
  },
  { additionalProperties: false },
);
export type OfficialPaymentAttemptCommandV1 = Static<typeof OfficialPaymentAttemptCommandV1Schema>;

export const OfficialPaymentRefundCommandV1Schema = Type.Object(
  {
    contractVersion: Type.Literal("1.0.0"),
    refundId: PaymentIdentifierSchema,
    attemptId: PaymentIdentifierSchema,
    tenantId: PaymentIdentifierSchema,
    storeId: PaymentIdentifierSchema,
    orderId: PaymentIdentifierSchema,
    orderNumber: OrderNumberSchema,
    businessDate: BusinessDateSchema,
    providerCode: ProviderCodeSchema,
    rail: OfficialPaymentRailSchema,
    providerTransactionReference: ProviderReferenceSchema,
    amountFen: MoneyFenSchema,
    currency: Type.Literal("CNY"),
    reason: Type.String({ minLength: 2, maxLength: 200 }),
    approvalId: PaymentIdentifierSchema,
    idempotencyKey: Type.String({ minLength: 8, maxLength: 128 }),
    operationTrace: BusinessOperationTraceV1Schema,
  },
  { additionalProperties: false },
);
export type OfficialPaymentRefundCommandV1 = Static<typeof OfficialPaymentRefundCommandV1Schema>;

export const OfficialPaymentRefundObservationV1Schema = Type.Object(
  {
    contractVersion: Type.Literal("1.0.0"),
    refundId: PaymentIdentifierSchema,
    attemptId: PaymentIdentifierSchema,
    providerCode: ProviderCodeSchema,
    rail: OfficialPaymentRailSchema,
    amountFen: MoneyFenSchema,
    currency: Type.Literal("CNY"),
    status: Type.Union([
      Type.Literal("PENDING"),
      Type.Literal("UNKNOWN"),
      Type.Literal("SUCCESS"),
      Type.Literal("CLOSED"),
      Type.Literal("FAILED"),
    ]),
    providerRefundReference: Type.Union([ProviderReferenceSchema, Type.Null()]),
    source: Type.Union([
      Type.Literal("REFUND_RESPONSE"),
      Type.Literal("QUERY_RESPONSE"),
      Type.Literal("VERIFIED_CALLBACK"),
    ]),
    verification: Type.Union([Type.Literal("AUTHENTICATED_API"), Type.Literal("SIGNED_CALLBACK")]),
    observedAt: CanonicalDateTimeSchema,
  },
  { additionalProperties: false },
);
export type OfficialPaymentRefundObservationV1 = Static<
  typeof OfficialPaymentRefundObservationV1Schema
>;

export const OfficialPaymentBillRequestV1Schema = Type.Object(
  {
    contractVersion: Type.Literal("1.0.0"),
    requestId: PaymentIdentifierSchema,
    tenantId: PaymentIdentifierSchema,
    storeId: PaymentIdentifierSchema,
    providerCode: ProviderCodeSchema,
    rail: OfficialPaymentRailSchema,
    businessDate: BusinessDateSchema,
    idempotencyKey: Type.String({ minLength: 8, maxLength: 128 }),
  },
  { additionalProperties: false },
);
export type OfficialPaymentBillRequestV1 = Static<typeof OfficialPaymentBillRequestV1Schema>;

export const OfficialPaymentBillLineV1Schema = Type.Object(
  {
    kind: Type.Union([Type.Literal("PAYMENT"), Type.Literal("REFUND")]),
    providerTransactionReference: ProviderReferenceSchema,
    attemptId: Type.Union([PaymentIdentifierSchema, Type.Null()]),
    amountFen: MoneyFenSchema,
    status: Type.Union([Type.Literal("SUCCESS"), Type.Literal("CLOSED")]),
    occurredAt: CanonicalDateTimeSchema,
  },
  { additionalProperties: false },
);
export type OfficialPaymentBillLineV1 = Static<typeof OfficialPaymentBillLineV1Schema>;

export const OfficialPaymentBillResultV1Schema = Type.Object(
  {
    contractVersion: Type.Literal("1.0.0"),
    requestId: PaymentIdentifierSchema,
    providerCode: ProviderCodeSchema,
    rail: OfficialPaymentRailSchema,
    businessDate: BusinessDateSchema,
    status: Type.Union([
      Type.Literal("READY"),
      Type.Literal("RESULT_UNKNOWN"),
      Type.Literal("FAILED"),
    ]),
    lines: Type.Array(OfficialPaymentBillLineV1Schema, { maxItems: 10_000 }),
    generatedAt: CanonicalDateTimeSchema,
    evidence: Type.Union([Type.Literal("SANDBOX"), Type.Literal("PROVIDER_ACCEPTED")]),
  },
  { additionalProperties: false },
);
export type OfficialPaymentBillResultV1 = Static<typeof OfficialPaymentBillResultV1Schema>;

export const OfficialPaymentObservationStatusSchema = Type.Union([
  Type.Literal("PENDING"),
  Type.Literal("UNKNOWN"),
  Type.Literal("SUCCESS"),
  Type.Literal("NOTPAY"),
  Type.Literal("CLOSED"),
  Type.Literal("FAILED"),
]);
export type OfficialPaymentObservationStatus = Static<
  typeof OfficialPaymentObservationStatusSchema
>;

export const OfficialPaymentObservationV1Schema = Type.Object(
  {
    contractVersion: Type.Literal("1.0.0"),
    attemptId: PaymentIdentifierSchema,
    providerCode: ProviderCodeSchema,
    rail: OfficialPaymentRailSchema,
    amountFen: MoneyFenSchema,
    currency: Type.Literal("CNY"),
    status: OfficialPaymentObservationStatusSchema,
    providerTransactionReference: Type.Union([ProviderReferenceSchema, Type.Null()]),
    source: Type.Union([
      Type.Literal("CREATE_RESPONSE"),
      Type.Literal("QUERY_RESPONSE"),
      Type.Literal("CANCEL_RESPONSE"),
      Type.Literal("VERIFIED_CALLBACK"),
    ]),
    verification: Type.Union([Type.Literal("AUTHENTICATED_API"), Type.Literal("SIGNED_CALLBACK")]),
    observedAt: CanonicalDateTimeSchema,
  },
  { additionalProperties: false },
);
export type OfficialPaymentObservationV1 = Static<typeof OfficialPaymentObservationV1Schema>;

export const OfficialPaymentAttemptStatusSchema = Type.Union([
  Type.Literal("PENDING"),
  Type.Literal("RESULT_UNKNOWN"),
  Type.Literal("SUCCESS"),
  Type.Literal("NOTPAY"),
  Type.Literal("CLOSED"),
  Type.Literal("FAILED_TERMINAL"),
  Type.Literal("MANUAL_REVIEW"),
]);
export type OfficialPaymentAttemptStatus = Static<typeof OfficialPaymentAttemptStatusSchema>;

export const OfficialPaymentAttemptV1Schema = Type.Object(
  {
    contractVersion: Type.Literal("1.0.0"),
    attemptId: PaymentIdentifierSchema,
    tenantId: PaymentIdentifierSchema,
    storeId: PaymentIdentifierSchema,
    orderId: PaymentIdentifierSchema,
    orderNumber: OrderNumberSchema,
    businessDate: BusinessDateSchema,
    providerCode: ProviderCodeSchema,
    rail: OfficialPaymentRailSchema,
    amountFen: MoneyFenSchema,
    expectedOrderVersion: Type.Integer({ minimum: 1 }),
    currency: Type.Literal("CNY"),
    status: OfficialPaymentAttemptStatusSchema,
    providerTransactionReference: Type.Union([ProviderReferenceSchema, Type.Null()]),
    lastObservationSource: Type.Union([
      Type.Literal("CREATE_RESPONSE"),
      Type.Literal("QUERY_RESPONSE"),
      Type.Literal("CANCEL_RESPONSE"),
      Type.Literal("VERIFIED_CALLBACK"),
      Type.Null(),
    ]),
    allowsAlternativePayment: Type.Boolean(),
    recognizedAsPaid: Type.Boolean(),
    requiresManualReview: Type.Boolean(),
    version: Type.Integer({ minimum: 1 }),
    createdAt: CanonicalDateTimeSchema,
    updatedAt: CanonicalDateTimeSchema,
    operationTrace: BusinessOperationTraceV1Schema,
  },
  { additionalProperties: false },
);
export type OfficialPaymentAttemptV1 = Static<typeof OfficialPaymentAttemptV1Schema>;

const SettlementDigestSchema = Type.String({ pattern: "^[0-9a-f]{32}$" });

export const OfficialPaymentSettlementCommandV1Schema = Type.Object(
  {
    contractVersion: Type.Literal("1.0.0"),
    settlementId: Type.TemplateLiteral([
      Type.Literal("payment_settlement:"),
      SettlementDigestSchema,
    ]),
    attempt: OfficialPaymentAttemptV1Schema,
    providerEvidence: Type.Literal("PROVIDER_ACCEPTED"),
    idempotencyKey: Type.TemplateLiteral([
      Type.Literal("official-payment-settle:"),
      SettlementDigestSchema,
    ]),
    operationTrace: BusinessOperationTraceV1Schema,
  },
  { additionalProperties: false },
);
export type OfficialPaymentSettlementCommandV1 = Static<
  typeof OfficialPaymentSettlementCommandV1Schema
>;

export const OfficialPaymentDirectiveV1Schema = Type.Object(
  {
    action: Type.Union([
      Type.Literal("WAIT_OR_QUERY_SAME_ATTEMPT"),
      Type.Literal("FINALIZE_CONFIRMED_PAYMENT"),
      Type.Literal("ALLOW_ALTERNATIVE_PAYMENT"),
      Type.Literal("REQUIRE_MANUAL_REVIEW"),
    ]),
    canCreateNewAttempt: Type.Boolean(),
    canRecognizeRevenue: Type.Boolean(),
  },
  { additionalProperties: false },
);
export type OfficialPaymentDirectiveV1 = Static<typeof OfficialPaymentDirectiveV1Schema>;

export const OfficialPaymentExecutionResultV1Schema = Type.Object(
  {
    contractVersion: Type.Literal("1.0.0"),
    attempt: OfficialPaymentAttemptV1Schema,
    directive: OfficialPaymentDirectiveV1Schema,
    settlementCommand: Type.Union([OfficialPaymentSettlementCommandV1Schema, Type.Null()]),
    providerAction: Type.Union([
      Type.Literal("NONE"),
      Type.Literal("CREATE"),
      Type.Literal("QUERY"),
      Type.Literal("CALLBACK"),
    ]),
    providerOutcome: Type.Union([
      Type.Literal("NOT_CALLED"),
      Type.Literal("RECORDED"),
      Type.Literal("RESULT_UNKNOWN"),
    ]),
    providerEvidence: Type.Union([Type.Literal("SANDBOX"), Type.Literal("PROVIDER_ACCEPTED")]),
  },
  { additionalProperties: false },
);
export type OfficialPaymentExecutionResultV1 = Static<
  typeof OfficialPaymentExecutionResultV1Schema
>;

export const OfficialPaymentStartRequestV1Schema = Type.Object(
  {
    rail: OfficialPaymentRailSchema,
    expectedTotalFen: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    idempotencyKey: Type.String({ minLength: 8, maxLength: 128 }),
  },
  { additionalProperties: false },
);
export type OfficialPaymentStartRequestV1 = Static<typeof OfficialPaymentStartRequestV1Schema>;

export const OfficialPaymentOperatorResultV1Schema = Type.Object(
  {
    contractVersion: Type.Literal("1.0.0"),
    attempt: OfficialPaymentAttemptV1Schema,
    directive: OfficialPaymentDirectiveV1Schema,
    providerEvidence: Type.Union([Type.Literal("SANDBOX"), Type.Literal("PROVIDER_ACCEPTED")]),
    gatewaySettlement: Type.Union([Type.Literal("NOT_SETTLED"), Type.Literal("SETTLED")]),
  },
  { additionalProperties: false },
);
export type OfficialPaymentOperatorResultV1 = Static<typeof OfficialPaymentOperatorResultV1Schema>;

function isCanonicalDateTime(value: string): boolean {
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) && new Date(timestamp).toISOString() === value;
}

function isOrderBoundOperation(input: {
  orderId: string;
  orderNumber: string;
  businessDate: string;
  idempotencyKey: string;
  operationTrace: unknown;
}): boolean {
  if (!isOrderNumberForOrderId(input.orderNumber, input.orderId, input.businessDate)) return false;
  if (!isBusinessOperationTraceV1(input.operationTrace)) return false;
  return (
    input.operationTrace.idempotencyKey === input.idempotencyKey &&
    input.operationTrace.businessNumber?.kind === "ORDER" &&
    input.operationTrace.businessNumber.value === input.orderNumber
  );
}

export function isOfficialPaymentCreateCommandV1(
  value: unknown,
): value is OfficialPaymentCreateCommandV1 {
  if (!Value.Check(OfficialPaymentCreateCommandV1Schema, value)) return false;
  const command = value as OfficialPaymentCreateCommandV1;
  return isCanonicalDateTime(command.expiresAt) && isOrderBoundOperation(command);
}

export function isOfficialPaymentAttemptCommandV1(
  value: unknown,
): value is OfficialPaymentAttemptCommandV1 {
  if (!Value.Check(OfficialPaymentAttemptCommandV1Schema, value)) return false;
  const command = value as OfficialPaymentAttemptCommandV1;
  return (
    isOrderBoundOperation(command) &&
    (command.action === "QUERY" || command.providerTransactionReference !== null)
  );
}

export function isOfficialPaymentRefundCommandV1(
  value: unknown,
): value is OfficialPaymentRefundCommandV1 {
  return (
    Value.Check(OfficialPaymentRefundCommandV1Schema, value) &&
    isOrderBoundOperation(value as OfficialPaymentRefundCommandV1)
  );
}

export function isOfficialPaymentRefundObservationV1(
  value: unknown,
): value is OfficialPaymentRefundObservationV1 {
  if (!Value.Check(OfficialPaymentRefundObservationV1Schema, value)) return false;
  const observation = value as OfficialPaymentRefundObservationV1;
  if (!isCanonicalDateTime(observation.observedAt)) return false;
  if (
    observation.source === "VERIFIED_CALLBACK"
      ? observation.verification !== "SIGNED_CALLBACK"
      : observation.verification !== "AUTHENTICATED_API"
  ) {
    return false;
  }
  return observation.status !== "SUCCESS" || observation.providerRefundReference !== null;
}

export function isOfficialPaymentObservationV1(
  value: unknown,
): value is OfficialPaymentObservationV1 {
  if (!Value.Check(OfficialPaymentObservationV1Schema, value)) return false;
  const observation = value as OfficialPaymentObservationV1;
  if (!isCanonicalDateTime(observation.observedAt)) return false;
  if (
    observation.source === "VERIFIED_CALLBACK"
      ? observation.verification !== "SIGNED_CALLBACK"
      : observation.verification !== "AUTHENTICATED_API"
  ) {
    return false;
  }
  if (observation.status === "SUCCESS" && observation.providerTransactionReference === null) {
    return false;
  }
  if (
    observation.source !== "CREATE_RESPONSE" &&
    observation.providerTransactionReference === null
  ) {
    return false;
  }
  return true;
}

export function isOfficialPaymentAttemptV1(value: unknown): value is OfficialPaymentAttemptV1 {
  if (!Value.Check(OfficialPaymentAttemptV1Schema, value)) return false;
  const attempt = value as OfficialPaymentAttemptV1;
  if (
    !isCanonicalDateTime(attempt.createdAt) ||
    !isCanonicalDateTime(attempt.updatedAt) ||
    Date.parse(attempt.updatedAt) < Date.parse(attempt.createdAt) ||
    !isOrderNumberForOrderId(attempt.orderNumber, attempt.orderId, attempt.businessDate) ||
    !isBusinessOperationTraceV1(attempt.operationTrace) ||
    attempt.operationTrace.businessNumber?.kind !== "ORDER" ||
    attempt.operationTrace.businessNumber.value !== attempt.orderNumber
  ) {
    return false;
  }
  const expected = officialPaymentAttemptFlags(attempt.status);
  return (
    attempt.allowsAlternativePayment === expected.allowsAlternativePayment &&
    attempt.recognizedAsPaid === expected.recognizedAsPaid &&
    attempt.requiresManualReview === expected.requiresManualReview
  );
}

export function isOfficialPaymentSettlementCommandV1(
  value: unknown,
): value is OfficialPaymentSettlementCommandV1 {
  if (!Value.Check(OfficialPaymentSettlementCommandV1Schema, value)) return false;
  const command = value as OfficialPaymentSettlementCommandV1;
  if (!isOfficialPaymentAttemptV1(command.attempt)) return false;
  const attempt = command.attempt;
  const digest = command.settlementId.slice("payment_settlement:".length);
  return (
    attempt.status === "SUCCESS" &&
    attempt.recognizedAsPaid &&
    attempt.providerTransactionReference !== null &&
    attempt.lastObservationSource !== null &&
    command.idempotencyKey === `official-payment-settle:${digest}` &&
    command.operationTrace.commandId === `command:payment:settle:${digest}` &&
    command.operationTrace.correlationId === attempt.operationTrace.correlationId &&
    command.operationTrace.causationId === attempt.operationTrace.commandId &&
    command.operationTrace.idempotencyKey === command.idempotencyKey &&
    command.operationTrace.businessNumber?.kind === "ORDER" &&
    command.operationTrace.businessNumber.value === attempt.orderNumber
  );
}

function expectedDirective(
  status: OfficialPaymentAttemptStatus,
  providerEvidence: OfficialPaymentExecutionResultV1["providerEvidence"],
): OfficialPaymentDirectiveV1 {
  if (status === "SUCCESS") {
    if (providerEvidence === "SANDBOX") {
      return {
        action: "REQUIRE_MANUAL_REVIEW",
        canCreateNewAttempt: false,
        canRecognizeRevenue: false,
      };
    }
    return {
      action: "FINALIZE_CONFIRMED_PAYMENT",
      canCreateNewAttempt: false,
      canRecognizeRevenue: true,
    };
  }
  if (status === "NOTPAY" || status === "CLOSED") {
    return {
      action: "ALLOW_ALTERNATIVE_PAYMENT",
      canCreateNewAttempt: true,
      canRecognizeRevenue: false,
    };
  }
  if (status === "FAILED_TERMINAL" || status === "MANUAL_REVIEW") {
    return {
      action: "REQUIRE_MANUAL_REVIEW",
      canCreateNewAttempt: false,
      canRecognizeRevenue: false,
    };
  }
  return {
    action: "WAIT_OR_QUERY_SAME_ATTEMPT",
    canCreateNewAttempt: false,
    canRecognizeRevenue: false,
  };
}

function sameAttempt(left: OfficialPaymentAttemptV1, right: OfficialPaymentAttemptV1): boolean {
  return (
    left.contractVersion === right.contractVersion &&
    left.attemptId === right.attemptId &&
    left.tenantId === right.tenantId &&
    left.storeId === right.storeId &&
    left.orderId === right.orderId &&
    left.orderNumber === right.orderNumber &&
    left.businessDate === right.businessDate &&
    left.providerCode === right.providerCode &&
    left.rail === right.rail &&
    left.amountFen === right.amountFen &&
    left.expectedOrderVersion === right.expectedOrderVersion &&
    left.currency === right.currency &&
    left.status === right.status &&
    left.providerTransactionReference === right.providerTransactionReference &&
    left.lastObservationSource === right.lastObservationSource &&
    left.allowsAlternativePayment === right.allowsAlternativePayment &&
    left.recognizedAsPaid === right.recognizedAsPaid &&
    left.requiresManualReview === right.requiresManualReview &&
    left.version === right.version &&
    left.createdAt === right.createdAt &&
    left.updatedAt === right.updatedAt &&
    left.operationTrace.commandId === right.operationTrace.commandId &&
    left.operationTrace.correlationId === right.operationTrace.correlationId &&
    left.operationTrace.causationId === right.operationTrace.causationId &&
    left.operationTrace.idempotencyKey === right.operationTrace.idempotencyKey &&
    left.operationTrace.businessNumber?.kind === right.operationTrace.businessNumber?.kind &&
    left.operationTrace.businessNumber?.value === right.operationTrace.businessNumber?.value
  );
}

export function isOfficialPaymentExecutionResultV1(
  value: unknown,
): value is OfficialPaymentExecutionResultV1 {
  if (!Value.Check(OfficialPaymentExecutionResultV1Schema, value)) return false;
  const result = value as OfficialPaymentExecutionResultV1;
  if (!isOfficialPaymentAttemptV1(result.attempt)) return false;
  const directive = expectedDirective(result.attempt.status, result.providerEvidence);
  if (
    result.directive.action !== directive.action ||
    result.directive.canCreateNewAttempt !== directive.canCreateNewAttempt ||
    result.directive.canRecognizeRevenue !== directive.canRecognizeRevenue ||
    (result.providerAction === "NONE") !== (result.providerOutcome === "NOT_CALLED")
  ) {
    return false;
  }
  if (result.settlementCommand === null) {
    return !(
      result.providerEvidence === "PROVIDER_ACCEPTED" &&
      directive.action === "FINALIZE_CONFIRMED_PAYMENT"
    );
  }
  return (
    result.providerEvidence === "PROVIDER_ACCEPTED" &&
    directive.action === "FINALIZE_CONFIRMED_PAYMENT" &&
    isOfficialPaymentSettlementCommandV1(result.settlementCommand) &&
    sameAttempt(result.attempt, result.settlementCommand.attempt)
  );
}

export function isOfficialPaymentOperatorResultV1(
  value: unknown,
): value is OfficialPaymentOperatorResultV1 {
  if (!Value.Check(OfficialPaymentOperatorResultV1Schema, value)) return false;
  const result = value as OfficialPaymentOperatorResultV1;
  if (!isOfficialPaymentAttemptV1(result.attempt)) return false;
  const directive = expectedDirective(result.attempt.status, result.providerEvidence);
  const shouldBeSettled =
    result.providerEvidence === "PROVIDER_ACCEPTED" &&
    directive.action === "FINALIZE_CONFIRMED_PAYMENT";
  return (
    result.directive.action === directive.action &&
    result.directive.canCreateNewAttempt === directive.canCreateNewAttempt &&
    result.directive.canRecognizeRevenue === directive.canRecognizeRevenue &&
    result.gatewaySettlement === (shouldBeSettled ? "SETTLED" : "NOT_SETTLED")
  );
}

export function officialPaymentAttemptFlags(status: OfficialPaymentAttemptStatus): {
  allowsAlternativePayment: boolean;
  recognizedAsPaid: boolean;
  requiresManualReview: boolean;
} {
  return {
    allowsAlternativePayment: status === "NOTPAY" || status === "CLOSED",
    recognizedAsPaid: status === "SUCCESS",
    requiresManualReview: status === "FAILED_TERMINAL" || status === "MANUAL_REVIEW",
  };
}
